import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import Animation from 'components/Generic/Animation';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { getCookie, setCookie } from 'hooks/useCookie';
import { useEffect, useState, useRef } from 'react';
import styles from './styles/StickyCta.module.scss';

const StickyCta = ({ fields, rendering, params }: ComponentProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const { anchorId } = useHelpers(rendering);
  const { t } = useI18n();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const stickyCta = useRef(null);

  const [showComponent, setShowComponent] = useState(false);
  const [showMobileCallButton, setShowMobileCallButton] = useState(false);

  const docHeightWithoutFooter =
    typeof window !== 'undefined' &&
    document?.body.clientHeight -
      +document?.querySelector('.main-footer').offsetHeight -
      window?.innerHeight;

  const halfScreenHeight = typeof window !== 'undefined' && window?.innerHeight / 2;
  const haveDesktopAnimation = !!fields?.desktopAnimation?.value;
  const haveMobileAnimation = !!fields?.mobileAnimation?.value;
  const getDesktopAnimation = fields?.desktopAnimation?.value;
  const getMobileAnimation = fields?.mobileAnimation?.value;
  const getBackground = params?.background || 'background-primary';
  const getIcon = fields?.ctaLogo?.value;
  const ctaLinkText = fields?.ctaLink?.value?.text;

  const onScroll = (event: any) => {
    const scrollTop = event.target.scrollingElement.scrollTop;

    if (scrollTop >= halfScreenHeight && !getCookie('abrdn-sticky-cta')) {
      setShowComponent(true);
    } else {
      setShowComponent(false);
    }

    if (event.target.defaultView.scrollY >= docHeightWithoutFooter && screen.width < 768) {
      setShowMobileCallButton(false);
    } else {
      setShowMobileCallButton(true);
    }
  };

  const close = () => {
    const value = {
      n: sitecoreContext.site.name,
      e: undefined,
      s: undefined,
      l: sitecoreContext.language,
    };
    setCookie('abrdn-sticky-cta', JSON.stringify(value));
    setShowComponent(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    ((showComponent && showMobileCallButton) || isExperienceEditor) && (
      <section
        className={styles['sticky-cta']}
        id={anchorId}
        data-e2e-component-uid={rendering.uid}
        ref={stickyCta}
      >
        <div className="d-none d-md-block">
          <div className="d-flex justify-content-end">
            <button
              aria-label={t('closeModal')}
              className={`btn ${styles['close-button']} d-flex align-items-center text-link`}
              onClick={close}
            >
              <span>{t('close')}</span>
              <em className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
          <div className={`text-white ${getBackground}`}>
            <div className="container d-flex justify-content-between align-items-center py-4">
              {haveDesktopAnimation && (
                <Animation
                  animationData={getDesktopAnimation}
                  visible={showComponent}
                  className={`${styles['sticky-cta-image']} flex-shrink-0`}
                  layout={{ fit: 'cover', align: [0.5, 0.5] }}
                />
              )}
              <Text field={fields.title} tag="h3" className="mx-5 mx-lg-6 flex-fill" />
              <AbrdnButton
                id="call-button"
                linkField={fields.ctaLink}
                showExternalIcon={false}
                className={`${
                  styles['btn-call']
                } d-flex align-items-center background-white flex-shrink-0 ps-3 pe-4 ${
                  isExperienceEditor ? 'flex-row-reverse' : ''
                }`}
              >
                <em
                  className={`bi bi-${getIcon} h4 d-flex align-items-center me-1`}
                  aria-hidden="true"
                />
                {!isExperienceEditor && <span className="button-label">{ctaLinkText}</span>}
              </AbrdnButton>
            </div>
          </div>
        </div>
        <div className="d-block d-md-none m-2 text-white">
          <AbrdnButton
            id="call-button-mobile"
            linkField={fields.ctaLink}
            showExternalIcon={false}
            className={styles['cta-mobile-button']}
          >
            {haveMobileAnimation && (
              <Animation
                animationData={getMobileAnimation}
                visible={showComponent}
                className={`${styles['mobile-call-button']} d-flex align-items-center ${getBackground}`}
              />
            )}
          </AbrdnButton>
        </div>
      </section>
    )
  );
};

export default StickyCta;
